import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { normalizeResponsePresentation, isStructuredMode } from './responsePresentation';
import { ResponseBlocks, ResponseSections } from './ResponseRichText';
import { deriveEditorialPresentation } from './markdownEditorial';
import CompactUtilityCard from './CompactUtilityCard';
import {
  AnalysisCard, DecisionCard, GenericStructuredCard, PlanCard, ReminderCard,
} from './StructuredResponseCards';
import './responseDesign.css';

function expansionKey(sessionId, messageId) {
  return `athena.response-design.expanded:${sessionId || 'local'}:${messageId || 'response'}`;
}

function useExpandedResponse(sessionId, messageId, initial = false) {
  const key = useMemo(() => expansionKey(sessionId, messageId), [sessionId, messageId]);
  const [expanded, setExpanded] = useState(() => {
    try { return sessionStorage.getItem(key) === 'true' || initial; } catch { return initial; }
  });

  useEffect(() => {
    try { setExpanded(sessionStorage.getItem(key) === 'true' || initial); } catch { setExpanded(initial); }
  }, [key, initial]);

  const toggle = () => setExpanded(current => {
    const next = !current;
    try { sessionStorage.setItem(key, String(next)); } catch { /* storage is optional */ }
    return next;
  });
  return [expanded, toggle];
}

function EditorialResponse({ presentation }) {
  const hasEditorialContent = Boolean(
    presentation.title || presentation.summary || presentation.message?.length || presentation.sections?.length,
  );

  if (!hasEditorialContent) {
    return presentation.utility ? <CompactUtilityCard utility={presentation.utility} /> : null;
  }

  return (
    <>
      <motion.article
        className="athena-editorial-response"
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        {(presentation.title || presentation.summary) && (
          <header>
            {presentation.title && <h1>{presentation.title}</h1>}
            {presentation.summary && <p className="athena-editorial-summary">{presentation.summary}</p>}
          </header>
        )}
        {presentation.message?.length > 0 && <ResponseBlocks blocks={presentation.message} />}
        {presentation.sections?.length > 0 && <ResponseSections sections={presentation.sections} variant="editorial" />}
      </motion.article>
      {presentation.utility && <CompactUtilityCard utility={presentation.utility} />}
    </>
  );
}

/**
 * One safe decision point for live envelopes and older Markdown history. The
 * Markdown adapter only accepts visibly structured answers; short messages
 * deliberately stay on the caller's normal chat fallback.
 */
export function resolveAdaptiveResponsePresentation(rawPresentation, content) {
  return normalizeResponsePresentation(rawPresentation) || deriveEditorialPresentation(content);
}

/**
 * The adaptive entry point. A bad/new/old response presentation intentionally
 * falls through to the caller's existing Markdown renderer.
 */
export function AdaptiveResponseRenderer({
  presentation: rawPresentation,
  content,
  streaming = false,
  sessionId,
  messageId,
  timestamp,
  onAction,
  fallback,
}) {
  const presentation = useMemo(
    () => resolveAdaptiveResponsePresentation(rawPresentation, content),
    [rawPresentation, content],
  );
  const [expanded, toggleExpanded] = useExpandedResponse(sessionId, messageId, false);

  if (streaming || !presentation) return fallback?.() || null;
  if (!isStructuredMode(presentation.mode)) return <EditorialResponse presentation={presentation} />;

  const cardProps = { presentation, expanded, onToggle: toggleExpanded, onAction, timestamp };
  if (presentation.mode === 'analysis') return <AnalysisCard {...cardProps} />;
  if (presentation.mode === 'plan') return <PlanCard {...cardProps} />;
  if (presentation.mode === 'reminder') return <ReminderCard {...cardProps} />;
  if (presentation.mode === 'decision') return <DecisionCard {...cardProps} />;
  return <GenericStructuredCard {...cardProps} content={content} />;
}
